import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Route, Redirect, Switch, NavLink, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getToken, setToken, hooks } from './api/http';
import { companiesApi } from './api/services';
import { setCompanies, setUser, NO_COMPANY } from './store';
import KeepAlive from './custom/KeepAlive';
import ThemeToggle from './components/common/ThemeToggle';
import Sezona from './components/common/Sezona';
import RadioPlayer from './components/common/RadioPlayer';
import AdminPodesavanja from './components/zabava/AdminPodesavanja';
import APP_VERSION from './version';
import { ZabavaTraka, ZabavaEfekti } from './components/zabava/Zabava';
import { ulogaIzTokena } from './components/zabava/zabavaTok';
import checkVersion from './custom/checkVersion';
import Login from './Login/Login';
import Register from './Login/Register';
import VerifyEmail from './Login/VerifyEmail';
import ForgotPassword from './Login/ForgotPassword';
import NewPassword from './Login/NewPassword';
import Logout from './Login/Logout';
import TachoReader from './components/TachoReader/TachoReader';
import TachoAnalyzer from './components/TachoAnalyzer/TachoAnalyzer';
import TachoBulk from './components/TachoBulk/TachoBulk';
import ImportVehicleData from './components/TachoReader/ImportVehicleData';
import Activities from './components/Activities/Activities';
import Drivers from './components/Drivers/Drivers';
import Vehicles from './components/Vehicles/Vehicles';
import Companies from './components/Companies/Companies';
import Reports from './components/Reports/Reports';
import Kazne from './components/Kazne/Kazne';
import Ocitavanja from './components/Ocitavanja/Ocitavanja';
import Akcize from './components/Akcize/Akcize';
import { useChatTok, useNeprocitano } from './components/Chat/chatTok';

/* Chat se učitava tek kad se otvori (emotikoni i mapa su veliki) */
const ChatEkran = lazy(() => import('./components/Chat/Chat'));
const Chat = (p) => (
    <Suspense fallback={<div className="chat-ucitava"><i className="pi pi-spin pi-spinner" aria-label="Učitavanje" /></div>}>
        <ChatEkran {...p} />
    </Suspense>
);

/* Redosled stavki je isti kao u staroj aplikaciji; grupe su samo vizuelni naslovi. */
const NAV = [
    { caption: 'Komunikacija', items: [
        { path: '/chat', label: 'Chat', icon: 'bi-chat-dots', znacka: 'chat' },
    ] },
    { caption: 'Unos', items: [
        { path: '/tacho', label: 'Taho čitač', icon: 'bi-speedometer2' },
        { path: '/tacho-analyzer', label: 'Taho listići', icon: 'bi-bullseye' },
        { path: '/tacho-bulk', label: 'Bulk listići', icon: 'bi-stack' },
    ] },
    { caption: 'Pregled', items: [
        { path: '/activities', label: 'Aktivnosti', icon: 'bi-play-circle' },
        { path: '/drivers', label: 'Vozači', icon: 'bi-person-vcard' },
        { path: '/vehicles', label: 'Vozila', icon: 'bi-truck' },
        { path: '/companies', label: 'Kompanije', icon: 'bi-building' },
    ] },
    { caption: 'Izveštaji', items: [
        { path: '/reports', label: 'Izveštaji', icon: 'bi-file-earmark-text' },
        { path: '/kazne', label: 'Kazne', icon: 'bi-exclamation-octagon' },
        { path: '/ocitavanja', label: 'Očitavanja', icon: 'bi-calendar2-check' },
        { path: '/akcize', label: 'Akcize', icon: 'bi-fuel-pump' },
    ] },
];

const NAV_KEY = 'navCollapsed';
const readCollapsed = () => {
    try { return localStorage.getItem(NAV_KEY) === '1'; } catch { return false; }
};
const initials = (u) => `${(u.firstName || '').charAt(0)}${(u.lastName || '').charAt(0)}`.toUpperCase() || '?';

const PAGES = [
    { path: '/tacho', component: TachoReader },
    { path: '/tacho-analyzer', component: TachoAnalyzer },
    { path: '/tacho-bulk', component: TachoBulk },
    { path: '/tacho-import-vehicle', component: ImportVehicleData },
    { path: '/activities', component: Activities },
    { path: '/drivers', component: Drivers },
    { path: '/vehicles', component: Vehicles },
    { path: '/companies', component: Companies },
    { path: '/reports', component: Reports },
    { path: '/kazne', component: Kazne },
    { path: '/ocitavanja', component: Ocitavanja },
    { path: '/akcize', component: Akcize },
    { path: '/chat', component: Chat },
];

/* broj nepročitanih poruka: značka na meniju + "(N)" u naslovu kartice i na ikonici aplikacije */
function ChatZnacka() {
    const n = useNeprocitano();
    useEffect(() => {
        const osnovni = document.title.replace(/^\(\d+\+?\)\s*/, '');
        document.title = n > 0 ? `(${n > 99 ? '99+' : n}) ${osnovni}` : osnovni;
        try { if (navigator.setAppBadge) { if (n > 0) navigator.setAppBadge(n); else navigator.clearAppBadge(); } } catch { /* nije podržano */ }
    }, [n]);
    if (!n) return null;
    return <span className="nav-znacka" aria-label={`${n} nepročitanih poruka`}>{n > 99 ? '99+' : n}</span>;
}

function Shell() {
    const [collapsed, setCollapsed] = useState(readCollapsed);
    const [mobileOpen, setMobileOpen] = useState(false);
    const history = useHistory();
    const { pathname } = useLocation();
    const dispatch = useDispatch();
    const user = useSelector((s) => s.auth.user);
    useChatTok(); // chat uživo (nepročitane, zvuk) na svim stranicama

    useEffect(() => {
        companiesApi.all().then((list) => dispatch(setCompanies([NO_COMPANY, ...list]))).catch(() => {});
    }, [dispatch]);

    /* mobilni meni se zatvara posle navigacije i na Esc */
    useEffect(() => { setMobileOpen(false); }, [pathname]);
    useEffect(() => {
        if (!mobileOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [mobileOpen]);

    const toggleCollapsed = () => setCollapsed((c) => {
        try { localStorage.setItem(NAV_KEY, c ? '0' : '1'); } catch { /* ignore */ }
        return !c;
    });

    return (
        <div className="app-shell">
            <a className="skip-link" href="#main-content">Pređi na sadržaj</a>
            <header className="app-topbar">
                <button type="button" className="icon-btn nav-burger" onClick={() => setMobileOpen((o) => !o)}
                    aria-expanded={mobileOpen} aria-controls="main-navigation" aria-label={mobileOpen ? 'Zatvori meni' : 'Otvori meni'}>
                    <i className={`bi ${mobileOpen ? 'bi-x-lg' : 'bi-list'}`} aria-hidden="true" />
                </button>
                <Sezona>
                    <div>
                        <span className="brand-name">Tacho Royal Team</span>
                        <span className="brand-sub">Evidencija i analiza aktivnosti vozača</span>
                    </div>
                </Sezona>
                <div className="topbar-spacer"><ZabavaTraka /></div>
                <div className="topbar-actions">
                    {ulogaIzTokena() === 'Admin' && <AdminPodesavanja />}
                    {/* verzija za sve korisnike — da se zna koja je verzija kod njih (keš, prijava problema) */}
                    <span className="app-verzija" title={`Verzija aplikacije ${APP_VERSION}`}>v{APP_VERSION}</span>
                    <RadioPlayer />
                    <ThemeToggle />
                    <span className="topbar-sep" aria-hidden="true" />
                    {user && (
                        <div className="user-chip" title={user.email || undefined}>
                            <span className="avatar" aria-hidden="true">{initials(user)}</span>
                            <span className="user-chip-text">
                                <span className="user-chip-name">{user.firstName} {user.lastName}</span>
                                {user.email && <span className="user-chip-mail">{user.email}</span>}
                            </span>
                        </div>
                    )}
                    <button type="button" className="logout-btn" onClick={() => history.push('/logout')} title="Odjavi se" aria-label="Odjavi se">
                        <i className="bi bi-box-arrow-right" aria-hidden="true" />
                        <span className="logout-label">Odjavi se</span>
                    </button>
                </div>
            </header>
            <div className="app-body">
                <nav id="main-navigation" aria-label="Glavna navigacija"
                    className={`side-nav${collapsed ? ' collapsed' : ''}${mobileOpen ? ' mobile-open' : ''}`}>
                    {NAV.map((g) => (
                        <React.Fragment key={g.caption}>
                            <div className="nav-caption">{g.caption}</div>
                            {g.items.map((n) => (
                                <NavLink key={n.path} to={n.path} className="nav-link" activeClassName="active" title={collapsed ? n.label : undefined}>
                                    <i className={`bi ${n.icon}`} aria-hidden="true" />
                                    <span className="nav-label">{n.label}</span>
                                    {n.znacka === 'chat' && <ChatZnacka />}
                                </NavLink>
                            ))}
                        </React.Fragment>
                    ))}
                    <button type="button" className="nav-toggle" onClick={toggleCollapsed} aria-expanded={!collapsed}
                        aria-label={collapsed ? 'Proširi meni' : 'Skupi meni'} title={collapsed ? 'Proširi meni' : 'Skupi meni'}>
                        <i className={`bi ${collapsed ? 'bi-chevron-double-right' : 'bi-chevron-double-left'}`} aria-hidden="true" />
                        <span className="nav-label">Skupi meni</span>
                    </button>
                </nav>
                {mobileOpen && <button type="button" className="nav-scrim" aria-label="Zatvori meni" onClick={() => setMobileOpen(false)} />}
                <main id="main-content" className="app-main" tabIndex={-1}>
                    <KeepAlive routes={PAGES} />
                </main>
                <ZabavaEfekti />
            </div>
        </div>
    );
}

export default function App() {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const [token, setTokenState] = useState(getToken());

    /* Nova verzija na serveru -> reload (proverava se pri pokretanju i pri povratku na tab). */
    useEffect(() => {
        checkVersion();
        const onVisible = () => { if (document.visibilityState === 'visible') checkVersion(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, []);

    /* Token se menja kroz Login/Logout; ovde ga prosleđujemo kroz custom event. */
    useEffect(() => {
        const sync = () => setTokenState(getToken());
        window.addEventListener('tacho-auth', sync);
        window.addEventListener('storage', sync);
        return () => {
            window.removeEventListener('tacho-auth', sync);
            window.removeEventListener('storage', sync);
        };
    }, []);

    /* 401 -> odjava i povratak na login sa returnUrl (stari klijent ovo nije radio). */
    useEffect(() => {
        hooks.onUnauthorized = () => {
            if (!getToken()) return;
            setToken(null);
            dispatch(setUser(null));
            setTokenState(null);
            history.replace(`/login?returnUrl=${encodeURIComponent(location.pathname + location.search)}`);
        };
        return () => { hooks.onUnauthorized = null; };
    }, [history, location.pathname, location.search, dispatch]);

    const loggedIn = !!token;
    const isPublic = ['/login', '/register', '/verify', '/confirm', '/password-recovery', '/verify-password'].some((p) => location.pathname.startsWith(p));

    if (location.pathname.startsWith('/logout')) return <Logout />;

    if (!loggedIn) {
        return (
            <Switch>
                <Route path="/login" component={Login} />
                <Route path="/register" component={Register} />
                <Route path="/verify" component={VerifyEmail} />
                <Route path="/confirm/:id/:code" component={VerifyEmail} />
                <Route path="/password-recovery" component={ForgotPassword} />
                <Route path="/verify-password/:id/:code" component={NewPassword} />
                <Route render={({ location: l }) => <Redirect to={`/login?returnUrl=${encodeURIComponent(l.pathname + l.search)}`} />} />
            </Switch>
        );
    }

    /* Ulogovan korisnik ne ostaje na login/register stranicama. */
    if (isPublic) return <Redirect to="/" />;
    if (location.pathname === '/') return <Redirect to="/tacho" />;
    if (!PAGES.some((p) => location.pathname === p.path)) return <Redirect to="/tacho" />;

    return <Shell />;
}
